import { Button, Icon, cn } from "@nexus/ui";
import { useState, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { getUserFacingApiMessage } from "@/lib/api-client";

export interface OrderScriptRowActionsProps {
  briefUrl?: string | null;
  uploadId?: string | null;
  showOpportunityLink?: boolean;
  onOpenOpportunity?: () => void;
  onDownloadAsset?: () => Promise<void>;
  downloadLabel?: "brief" | "script";
  className?: string;
}

export function OrderScriptRowActions({
  briefUrl,
  uploadId,
  showOpportunityLink = false,
  onOpenOpportunity,
  onDownloadAsset,
  downloadLabel = "script",
  className,
}: OrderScriptRowActionsProps) {
  const { t } = useTranslation();
  const [downloading, setDownloading] = useState(false);
  const hasUpload = Boolean(uploadId?.trim());
  const hasBriefUrl = Boolean(briefUrl?.trim());

  if (!hasUpload && !hasBriefUrl && !showOpportunityLink) return null;

  const handleDownload = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (!onDownloadAsset || downloading) return;
    setDownloading(true);
    try {
      await onDownloadAsset();
    } catch (error) {
      toast.error(getUserFacingApiMessage(error));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className={cn("flex shrink-0 flex-col items-stretch gap-1.5", className)}
    >
      {hasUpload && onDownloadAsset ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={downloading}
          onClick={(event) => void handleDownload(event)}
        >
          {downloading
            ? t("marketplace.orders.detail.viewScriptDownloading", "Opening…")
            : downloadLabel === "brief"
              ? t("marketplace.orders.detail.downloadBrief", "Download brief")
              : t(
                  "marketplace.orders.detail.downloadScript",
                  "Download script",
                )}
        </Button>
      ) : null}
      {hasBriefUrl ? (
        <a
          href={briefUrl!}
          target="_blank"
          rel="noreferrer"
          onClick={(event) => event.stopPropagation()}
          className="inline-flex items-center justify-center gap-1 rounded-md border border-border px-2 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary/5 hover:text-primary/90"
        >
          {t("marketplace.orders.detail.viewScriptLink", "View script link")}
          <Icon
            name="arrow-up-right"
            size="xs"
            className="text-current"
            aria-hidden
          />
        </a>
      ) : null}
      {showOpportunityLink && onOpenOpportunity ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          iconEnd="arrow-up-right"
          onClick={(event) => {
            event.stopPropagation();
            onOpenOpportunity();
          }}
        >
          {t(
            "marketplace.orders.detail.viewScriptOpportunity",
            "View script's opportunity",
          )}
        </Button>
      ) : null}
    </div>
  );
}
